import { FormatDate } from '@/components/PrettyDate';
import styles from '@/components/ProjectDetails/Cards/Items/index.less';
import ServiceRequestAction from '@/components/Lead/ServiceRequestAction';
import { Space } from 'antd';
import classNames from 'classnames';
import { ServiceRequestModel } from '@/typings/models/ServiceRequest';
import { useDispatch } from 'umi';
import AppointmentStatusBadge from '@/components/ProjectDetails/Cards/Items/AppointmentStatusBadge';
import DateIcon from '@/components/DateIcon';
import { LeadDetailsModel } from '@/typings/models/Lead';
import { ClockCircleOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { F } from '@/utils/intl';
import { useOverflowTooltip } from '@/utils/hooks';

export default function AppointmentItem({
  appointment,
  refresh,
  lead,
  fadePastEvent,
  nextAction,
  highlighted,
}: {
  appointment: ServiceRequestModel;
  lead: LeadDetailsModel;
  nextAction?: boolean;
  refresh: () => void;
  fadePastEvent?: boolean;
  highlighted?: boolean;
}) {
  const dispatch = useDispatch();

  const titleText = appointment.title || appointment.branch_service?.service_type?.name;
  const titleNode = useOverflowTooltip(titleText);

  const getTitle = () => {
    if (appointment.blocker)
      return (
        <div
          className={classNames(
            styles.calendarEventTitle,
            styles.projectDetailItemTitle,
          )}
        >
          {titleNode}
        </div>
      );
    return (
      <Space size={4} className={styles.projectDetailItemTitle}>
        <AppointmentStatusBadge status={appointment.status} />
        {titleNode}
      </Space>
    );
  };

  const handleAppointmentClick = () => {
    if (appointment.blocker)
      return dispatch({
        type: 'serviceRequest/toggleCalendarEventDetailsDrawer',
        payload: { calendarEventId: appointment.id },
      });
    dispatch({
      type: 'serviceRequest/toggleDetailsDrawer',
      payload: { appointmentId: appointment.id },
    });
  };

  const isPast = new Date(appointment.utc_start_time) < new Date();

  return (
    <div
      onClick={handleAppointmentClick}
      role="item-container"
      data-testid="project-appointment-item"
      style={appointment.blocker && appointment.color ? { '--event-color': appointment.color } : undefined}
      className={classNames(
        styles.projectDetailItemContainer,
        styles.appointmentItemContainer,
        styles.projectDetailItemContainerClickable,
        {
          [styles.calendarEventItem]: appointment.blocker && !nextAction,
          [styles.pastAppointmentItemContainer]: fadePastEvent && isPast,
          [styles.highlightedItemContainer]: highlighted,
          [styles.nextActionItemContainer]: nextAction,
        },
      )}
    >
      {nextAction ? (
        <div className={styles.projectDetailItemContent}>
          <div className={styles.nextActionItemIcon}>
            <ClockCircleOutlined />
          </div>
          <div>
            <div className={styles.projectDetailItemHeader}>
              <div className={styles.nextActionTitle}>
                <F id="pages.lead.next_action" defaultMessage="Next Action" />
              </div>
              {getTitle()}
            </div>
            <div className={styles.projectDetailItemBody}>
              <Space size={6}>
                <DateIcon date={appointment.utc_start_time} />
                <FormatDate date={appointment.utc_start_time} appointmentDate />
              </Space>
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className={styles.appointmentCardTimeCol}>
            <span className={styles.appointmentCardDate}>
              <FormatDate format="D. MMM" date={appointment.utc_start_time} />
            </span>
            <span className={styles.appointmentCardTime}>
              <FormatDate date={appointment.utc_start_time} onlyTime />
            </span>
          </div>
          <div className={styles.projectDetailItemContent}>
            <div className={styles.projectDetailItemHeader}>{getTitle()}</div>
            {appointment.branch_service?.branch?.name && (
              <div className={styles.appointmentCardMeta}>
                <EnvironmentOutlined />
                <span>{appointment.branch_service.branch.name}</span>
              </div>
            )}
          </div>
        </>
      )}
      <div className={styles.projectDetailItemActions}>
        <ServiceRequestAction
          serviceRequest={appointment}
          lead={appointment.lead || lead}
          onActionEnd={refresh}
          allActionsInDropdown
        />
      </div>
    </div>
  );
}
